import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";

const ERA_OPTIONS = ["All", "1980s", "1990s", "2000s"];
const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const era = searchParams.get("era") || "All";
  const category = searchParams.get("category") || "All";
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(products.map((p) => p.category))], [products]);

  const filtered = useMemo(() => {
    let list = products.filter(
      (p) => (era === "All" || p.era === era) && (category === "All" || p.category === category)
    );
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, era, category, sort]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const chipCls = (active, activeBg) =>
    `rounded-full border-2 border-charcoal px-4 py-2 font-display text-xs font-bold uppercase tracking-tight transition-[transform,background-color,color] duration-150 hover:-translate-y-0.5 ${
      active ? `${activeBg} shadow-hard-sm` : "bg-white"
    }`;

  return (
    <div data-testid="shop-page" className="bg-cream">
      <section className="border-b-2 border-charcoal bg-blue py-14 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="font-mono text-xs font-bold uppercase tracking-wide text-yellow">The full catalogue</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Shop analog
          </h1>
          <p className="mt-3 max-w-lg text-white/85">
            Every piece links out to a real retailer. We don't hold stock — we hold opinions.
          </p>
        </div>
      </section>

      <section className="border-b-2 border-charcoal bg-white py-5">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 sm:px-6">
          <div className="flex flex-wrap items-center gap-2" data-testid="era-filters">
            <span className="mr-2 font-mono text-xs font-bold uppercase text-charcoal/60">Era:</span>
            {ERA_OPTIONS.map((e) => (
              <button key={e} onClick={() => setParam("era", e)} data-testid={`filter-era-${e.toLowerCase()}`} className={chipCls(era === e, "bg-blue text-white")}>
                {e === "2000s" ? "Y2K" : e}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="category-filters">
            <span className="mr-2 font-mono text-xs font-bold uppercase text-charcoal/60">Category:</span>
            {categories.map((c) => (
              <button key={c} onClick={() => setParam("category", c)} data-testid={`filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`} className={chipCls(category === c, "bg-coral text-white")}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs font-bold uppercase text-charcoal/60">Sort:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-select"
              className="rounded-full border-2 border-charcoal bg-cream px-4 py-2 font-display text-xs font-bold uppercase"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <span data-testid="shop-count" className="font-mono text-xs text-charcoal/60">{filtered.length} items</span>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {loading ? (
            <p className="font-mono text-sm" data-testid="shop-loading">Loading the vault...</p>
          ) : filtered.length === 0 ? (
            <div data-testid="shop-empty" className="card-chunky mx-auto max-w-md p-10 text-center">
              <p className="font-display text-2xl font-extrabold uppercase">Nothing here</p>
              <p className="mt-2 text-sm text-charcoal/70">The algorithm has no power over you. Try a different filter.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={Math.min(i * 0.04, 0.3)}>
                  <ProductCard product={p} stamp={p.rare} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
